<template>
  <div class="relative" ref="searchContainerRef">
    <div class="relative">
      <Search class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        type="text"
        placeholder="Quick fellow lookup..."
        class="pl-9 pr-9 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cosmicPurple focus:border-transparent w-full sm:w-72 transition-shadow"
        v-model="searchQuery"
        @input="onSearchInput"
        @keydown="onKeyDown"
        @focus="showResults = true"
      />
      <Loader2
        v-if="adventurersStore.isSearching"
        class="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2"
      />
      <button
        v-else-if="searchQuery"
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        title="Clear"
        @click="resetSearch"
      >
        <X class="w-3.5 h-3.5" />
      </button>
    </div>

    <!-- Dropdown: search results, or recent fellows when the input is empty -->
    <div
      v-if="showResults && (searchQuery || recentFellows.length)"
      class="absolute z-50 mt-2 w-full sm:w-[32rem] bg-white rounded-lg shadow-xl ring-1 ring-black/5 max-h-96 overflow-y-auto right-0 sm:left-auto left-0"
    >
      <div
        v-if="!searchQuery"
        class="flex items-center justify-between px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
      >
        <span class="flex items-center gap-1.5"><Clock class="w-3 h-3" /> Recent</span>
        <button type="button" class="normal-case font-medium hover:text-gray-600" @click="recentFellows = []">
          Clear
        </button>
      </div>

      <div v-if="rows.length > 0" class="py-1">
        <div
          v-for="(row, index) in rows"
          :key="row.id"
          role="button"
          tabindex="-1"
          @click="selectRow(row)"
          @mouseenter="selectedIndex = index"
          :class="[
            'mx-1 px-3 py-2.5 flex items-center gap-3 rounded-md cursor-pointer group transition-colors',
            selectedIndex === index ? 'bg-cosmicPurple/5' : ''
          ]"
        >
          <img
            v-if="row.avatar"
            :src="row.avatar"
            class="w-9 h-9 rounded-full object-cover ring-1 ring-gray-200"
            alt=""
          />
          <div v-else class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center ring-1 ring-gray-200">
            <User class="w-4 h-4 text-gray-400" />
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-900 truncate">{{ row.name }}</span>
              <span class="shrink-0 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                {{ row.batch }}
              </span>
            </div>
            <div class="text-xs text-gray-500 truncate">{{ row.email }}</div>
          </div>

          <ArrowRight class="w-4 h-4 text-gray-300 group-hover:text-cosmicPurple transition-colors" />
        </div>
      </div>
      <div v-else-if="!adventurersStore.isSearching" class="px-4 py-6 text-sm text-gray-500 text-center">
        No fellows found
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAdventurersStore, type BatchResult } from '@/stores/adventurers'
import { Search, Loader2, User, ArrowRight, X, Clock } from 'lucide-vue-next'
import { useDebounce } from '@/composables/useDebounce'
import { onClickOutside, useLocalStorage } from '@vueuse/core'

interface FellowRow {
  id: number
  name: string
  email: string
  batch: string
  avatar?: string
}

const MAX_RECENT = 5

const router = useRouter()
const adventurersStore = useAdventurersStore()

// Search State
const searchQuery = ref('')
const showResults = ref(false)
const searchContainerRef = ref(null)
const selectedIndex = ref(-1)
const { debounce } = useDebounce(500)

const recentFellows = useLocalStorage<FellowRow[]>('launchpad:recent-fellow-searches', [])

const searchRows = computed<FellowRow[]>(() =>
  adventurersStore.searchResults
    .flatMap((batch: BatchResult) => batch.adventurers)
    // Rows without a user id can't be navigated to, so don't render dead entries
    .filter((a) => a.profile_details?.user_details?.id)
    .map((a) => {
      const u = a.profile_details.user_details
      return {
        id: u.id,
        name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username || `User ${u.id}`,
        email: u.email ?? '',
        batch: a.batch ? a.batch.replace(/_/g, ' ') : 'No Batch',
        avatar: u.avatar_image
      }
    })
)

const rows = computed(() => (searchQuery.value.trim() ? searchRows.value : recentFellows.value))

const handleSearch = async () => {
  if (!searchQuery.value.trim()) {
    adventurersStore.clearSearch()
    return
  }
  await adventurersStore.searchAdventurers(searchQuery.value.trim())
  showResults.value = true
}

const debouncedSearch = debounce(handleSearch)

const onSearchInput = () => {
  selectedIndex.value = -1
  showResults.value = true
  if (!searchQuery.value.trim()) {
    adventurersStore.clearSearch()
    return
  }
  debouncedSearch()
}

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showResults.value = false
    selectedIndex.value = -1
    return
  }
  if (!showResults.value || !rows.value.length) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      selectedIndex.value = Math.min(selectedIndex.value + 1, rows.value.length - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
      break
    case 'Enter':
      event.preventDefault()
      selectRow(rows.value[Math.max(selectedIndex.value, 0)])
      break
  }
}

const resetSearch = () => {
  searchQuery.value = ''
  selectedIndex.value = -1
  adventurersStore.clearSearch()
}

const selectRow = (row: FellowRow) => {
  recentFellows.value = [row, ...recentFellows.value.filter((r) => r.id !== row.id)].slice(0, MAX_RECENT)
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/manage/fellow/${row.id}`)
  showResults.value = false
  resetSearch()
}

onClickOutside(searchContainerRef, () => {
  showResults.value = false
  selectedIndex.value = -1
})
</script>
